import { useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

interface FAQ {
  question?: string | null;
  answer?: string | null;
  category?: string | null;
  [key: string]: unknown;
}

function hasValue(val: unknown): val is string {
  if (val == null) return false;
  const s = String(val).trim().toLowerCase();
  return s !== '' && s !== 'false' && s !== 'null' && s !== 'undefined';
}

// ============================================================================
// FAQ item — a single accordion row with click-to-expand behavior.
// Keyboard support comes free via the native <button>.
// ============================================================================

interface FAQItemProps {
  item: FAQ;
  itemId: string;
  defaultOpen?: boolean;
}

function FAQItem({ item, itemId, defaultOpen }: FAQItemProps) {
  const [open, setOpen] = useState(Boolean(defaultOpen));
  const question = item.question || '';
  const answer = item.answer || '';

  if (!hasValue(question) && !hasValue(answer)) return null;

  return (
    <div className={`faq-item${open ? ' faq-item--open' : ''}`}>
      <button
        type="button"
        className="faq-item-trigger"
        aria-expanded={open}
        aria-controls={`${itemId}-answer`}
        id={`${itemId}-question`}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="faq-item-question">{question}</span>
        <span className="faq-item-icon" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>
      <div
        id={`${itemId}-answer`}
        role="region"
        aria-labelledby={`${itemId}-question`}
        className="faq-item-answer"
        hidden={!open}
      >
        {hasValue(answer) ? (
          <div
            className="faq-item-answer-body"
            dangerouslySetInnerHTML={{ __html: answer }}
          />
        ) : null}
      </div>
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

const UNCATEGORIZED_KEY = '__faq_uncategorized__';

export function FaqSection() {
  const faqs = useCollectionBinding<FAQ>('faqsection.items', {
    collection: 'faqs',
  });

  // Group FAQs by category. Items without a category fall into a single
  // bucket that renders without a category heading. If no items declare a
  // category we render the whole list as a flat accordion.
  const grouped = useMemo(() => {
    const map = new Map<string, FAQ[]>();
    for (const faq of faqs ?? []) {
      const key = hasValue(faq.category) ? faq.category!.trim() : UNCATEGORIZED_KEY;
      const bucket = map.get(key);
      if (bucket) bucket.push(faq);
      else map.set(key, [faq]);
    }
    const keys = Array.from(map.keys()).sort((a, b) => {
      if (a === UNCATEGORIZED_KEY) return 1;
      if (b === UNCATEGORIZED_KEY) return -1;
      return a.localeCompare(b);
    });
    return keys.map((key) => ({
      key,
      label: key === UNCATEGORIZED_KEY ? null : key,
      items: map.get(key)!,
    }));
  }, [faqs]);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const hasFaqs = (faqs ?? []).length > 0;

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up py-16 md:py-24 bg-[var(--vibe-color-background)]"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {hasFaqs ? (
          <div className="faq-groups">
            {grouped.map((group, groupIndex) => (
              <section className="faq-group" key={group.key}>
                {group.label && (
                  <h3 className="faq-group-title">{group.label}</h3>
                )}
                <div className="faq-list">
                  {group.items.map((item, itemIndex) => (
                    <FAQItem
                      key={`${group.key}-${itemIndex}`}
                      item={item}
                      itemId={`faq-${groupIndex}-${itemIndex}`}
                      defaultOpen={groupIndex === 0 && itemIndex === 0}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
            <p className="font-body text-lg">
              Frequently asked questions will be displayed here.
            </p>
          </div>
        )}
      </div>

      <style>{`
        .faq-groups {
          display: flex;
          flex-direction: column;
          gap: 2.5rem;
        }

        .faq-group-title {
          font-family: var(--vibe-font-heading);
          font-size: 1.1rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.14em;
          color: var(--vibe-color-text);
          margin: 0 0 1rem;
          padding-bottom: 0.6rem;
          border-bottom: 2px solid var(--vibe-color-primary);
        }

        .faq-list {
          display: flex;
          flex-direction: column;
          border-top: 1px solid var(--vibe-color-border);
        }

        .faq-item {
          border-bottom: 1px solid var(--vibe-color-border);
        }

        .faq-item-trigger {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1.25rem;
          width: 100%;
          padding: 1.1rem 0.25rem;
          background: transparent;
          border: 0;
          text-align: left;
          cursor: pointer;
          font-family: var(--vibe-font-heading);
          font-size: 1.02rem;
          font-weight: 600;
          color: var(--vibe-color-text);
          transition: color 0.2s;
        }
        .faq-item-trigger:hover {
          color: var(--vibe-color-primary);
        }
        .faq-item-trigger:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: 2px;
          border-radius: 2px;
        }

        .faq-item-question {
          flex: 1;
          line-height: 1.4;
        }

        .faq-item-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          flex-shrink: 0;
          color: var(--vibe-color-primary);
          transition: transform 0.25s ease;
        }
        .faq-item--open .faq-item-icon {
          transform: rotate(180deg);
        }

        .faq-item-answer {
          padding: 0 0.25rem 1.3rem;
        }

        .faq-item-answer-body {
          font-family: var(--vibe-font-body);
          font-size: 0.96rem;
          line-height: 1.65;
          color: var(--vibe-color-text-muted);
        }
        .faq-item-answer-body :where(p) {
          margin: 0 0 0.75rem;
        }
        .faq-item-answer-body :where(p:last-child) {
          margin-bottom: 0;
        }
        .faq-item-answer-body :where(ul) {
          list-style: disc;
          padding-left: 1.25rem;
          margin: 0 0 0.75rem;
        }
        .faq-item-answer-body :where(ol) {
          list-style: decimal;
          padding-left: 1.25rem;
          margin: 0 0 0.75rem;
        }
        .faq-item-answer-body :where(li) {
          margin-bottom: 0.3rem;
        }
        .faq-item-answer-body :where(a) {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        @media (prefers-reduced-motion: reduce) {
          .faq-item-icon {
            transition: none;
          }
        }

        @media (max-width: 768px) {
          .faq-item-trigger {
            font-size: 0.98rem;
            padding: 1rem 0.1rem;
          }
          .faq-group-title {
            font-size: 1rem;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FaqSection',
  displayName: 'FAQ List',
  tokens: {},
  collectionBindings: {
    'faqsection.items': {
      label: 'FAQs',
      collection: 'faqs',
    },
  },
};

(FaqSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FaqSection;
